import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Link,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
import { Project } from "../../types/project";
import { Company } from "../../types/company";
import { useNavigate } from "react-router-dom";
import { useGetProjectsQuery } from "../../services/project.service";
import dayjs from "dayjs";
import ChipProjectStatus from "../../components/ChipProjectStatus";
import ProjectDrawer from "../project/ProjectDrawer";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";


const CompanyProjectsPanel = ({ data: dataCompany }: { data?: Company; }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Project | null>(null);

  const onView = React.useCallback((item: any) => {
    setCurrentItem(item);
    setOpen(true);
  }, []);

  const { data, isLoading, isFetching } = useGetProjectsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    company: dataCompany?.id,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: isMobile ? 0 : 1,
        minWidth: isMobile ? 200 : 200,
        sortable: true,
        filterable: true,
        renderCell: (params: GridRenderCellParams<undefined, Project>) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => onView(params.row)}
            sx={{
              color: "#F4A259",
              textDecoration: "none",
              fontWeight: 500,
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            #{params.row.id} - {params.row.title}
          </Link>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 130,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, Project>) => (
          <ChipProjectStatus status={params.row.status} />
        ),
      },
      {
        field: "customer_id",
        headerName: isMobile ? "Customer/Location" : "Customer / Location",
        headerAlign: "center",
        minWidth: isMobile ? 150 : 250,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, Project>) =>
          !!params.row.customer ? (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
              {params.row.customer.first_name} {params.row.customer.last_name}
            </Typography>
          ) : !!params.row.location ? (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
              {params.row.location.name}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>n/a</Typography>
          ),
      },
      {
        field: "manager_id",
        headerName: "Manager",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 130,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, Project>) =>
          !!params.row.manager ? (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
              {params.row.manager.first_name} {params.row.manager.last_name}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>n/a</Typography>
          ),
      },
      {
        field: "is_private",
        headerName: "Private",
        headerAlign: "center",
        minWidth: isMobile ? 60 : 80,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, Project>) =>
          !!params.row.is_private ? (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>Yes</Typography>
          ) : (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>No</Typography>
          ),
      },
      {
        field: "start_at",
        headerName: "Start At",
        headerAlign: "center",
        minWidth: isMobile ? 140 : 200,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, Project>) =>
          `${dayjs(params.row.start_date).format("MMM DD, YYYY hh:mm A")}`,
        renderCell: (params: GridRenderCellParams<undefined, Project>) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
    ],
    [isMobile, onView]
  );

  return (
    <>
      <CardBase
        title="Jobs"
        useGlassCard={false}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
              }}
            />
            {!!query && (
              <Button
                variant="text"
                onClick={() => setQuery("")}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "auto" },
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                  },
                }}
              >
                Reset
              </Button>
            )}
          </Stack>
        }
        sx={{
          height: { xs: "70vh", sm: "65vh" },
          p: 0,
        }}
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={isMobile ? "calc(70vh - 100px)" : "calc(65vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: { xs: "0.875rem", sm: "0.95rem" },
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: "auto",
            },
            "& .MuiDataGrid-main": {
              overflowX: "auto",
            },
          }}
        />
      </CardBase>
      <ProjectDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </>
  );
};
export default CompanyProjectsPanel;
